import { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { PiggyBank, TrendingDown, Wallet, FileText, CalendarClock, Info, Lock } from 'lucide-react';
import { useVertical } from '../../context/VerticalContext';
import { MetricCard } from './MetricCard';
import { useFinancialData } from '../../hooks/useFinancialData';
import { supabase } from '../../lib/supabase';
import type { ProfitAndLossData } from '../../types/finance';
import { RecordDrawingModal } from './RecordDrawingModal';
import { ThisMonthCard } from './ThisMonthCard';
import { JobsToDoCard } from './JobsToDoCard';
import { AppointmentsCard } from './AppointmentsCard';
import { QuickActions } from './QuickActions';
import { BookingModal } from './BookingModal';
import { NeedsAttentionCard } from './NeedsAttentionCard';
import NewJobFlow from '../../modules/mechanic/Workshop/NewJobFlow';
import { ResumeJobBanner } from './ResumeJobBanner';
import { DiagnosticModal } from '../../modules/mechanic/Workshop/Diagnostics/DiagnosticModal';
import QuoteBuilder from '../../modules/mechanic/Quotes/QuoteBuilder';
import {
    incomeTaxBandLabel,
    nicBandLabel,
    vatBandLabel,
    currentTaxYearLabel,
    selfAssessmentDueLabel,
    taxYearStartLabel,
} from '../../lib/taxBands';
import { localDate, monthKey, monthLabel, monthRange, previousMonthKey } from '../../lib/periods';

/**
 * Dashboard grid — mobile-first single column; on md+ a 2-column layout
 * where Safe to Spend takes the left column at full height and the right
 * column stacks: [This Month | Expenses], Today's Jobs, Needs Attention.
 *
 * Structure is vertical-agnostic — labels for the Today's Jobs card use the
 * jargon system. Mechanic-specific VRM plate styling lives inside that card.
 */
export const PowerStack = ({ setup }: { setup?: React.ReactNode }) => {
    const { activeOrgId } = useVertical();
    const navigate = useNavigate();
    const location = useLocation();

    const {
        safe_to_spend,
        safe_to_spend_net,
        is_underfunded,
        underfunded_by,
        working_capital,
        tax_pot,
        ni_pot,
        vat_pot,
        raw_vat_liability,
        vat_net_position,
        gross_expenses,
        total_income,
        total_expenses,
        taxable_profit,
        tax_region,
        is_vat_registered,
        turnover_rolling_12m,
        vat_threshold,
        isHistoricalView,
        isMonthView,
        selectedMonth,
        drawings,
        refresh,
        pot_status,
        snapshot_status,
        loading: dataLoading,
    } = useFinancialData();

    // The engine reports a status per pot. When Safe to Spend or the tax and NI
    // it is built from could not be worked out, the figures are placeholders,
    // not zeros — say so instead of showing £0 or "complete your first job".
    const moneyUnavailable =
        !dataLoading &&
        (snapshot_status === 'error' ||
            pot_status.safe_to_spend === 'error' ||
            pot_status.tax_pot === 'error' ||
            pot_status.ni_pot === 'error');

    // The month the month-scoped cards describe: the selected one, or the
    // calendar month in the tax-year view.
    const viewMonth = selectedMonth ?? monthKey(new Date());
    const viewMonthLabel = monthLabel(viewMonth);

    // VAT registration is mandatory within 30 days of crossing the £90k
    // rolling-12m threshold. Surface as a warning the moment turnover passes
    // the threshold AND the org hasn't registered yet — HMRC compliance gap.
    const vatBreached =
        !isHistoricalView &&
        !is_vat_registered &&
        (turnover_rolling_12m ?? 0) > (vat_threshold ?? 0) &&
        (vat_threshold ?? 0) > 0;
    const vatOverBy = vatBreached
        ? (turnover_rolling_12m ?? 0) - (vat_threshold ?? 0)
        : 0;

    const [loading, setLoading] = useState(true);
    const [pnlData, setPnlData] = useState<ProfitAndLossData | null>(null);
    const [lastMonthPnl, setLastMonthPnl] = useState<ProfitAndLossData | null>(null);
    const [totalRecurring, setTotalRecurring] = useState(0);
    const [partsCashSpend, setPartsCashSpend] = useState(0);
    const [isDrawingModalOpen, setIsDrawingModalOpen] = useState(false);
    const [isBookingModalOpen, setIsBookingModalOpen] = useState(false);
    const [isNewJobModalOpen, setIsNewJobModalOpen] = useState(false);
    // Set when "Resume job" reopens a job started earlier from New Job.
    const [resumeJobId, setResumeJobId] = useState<string | null>(null);
    const [isDiagnosticModalOpen, setIsDiagnosticModalOpen] = useState(false);
    const [isQuoteModalOpen, setIsQuoteModalOpen] = useState(false);
    const [todayRefreshKey, setTodayRefreshKey] = useState(0);

    // Deep links from the quick-action buttons / redirects. `?action=new_job`,
    // `?action=new_diagnostic` and `?action=new_quote` open the matching modal,
    // then the param is cleared so a refresh doesn't reopen it.
    useEffect(() => {
        const params = new URLSearchParams(location.search);
        const action = params.get('action');
        if (action === 'new_job') setIsNewJobModalOpen(true);
        if (action === 'new_diagnostic') setIsDiagnosticModalOpen(true);
        if (action === 'new_quote') setIsQuoteModalOpen(true);
        if (action === 'new_job' || action === 'new_diagnostic' || action === 'new_quote') {
            window.history.replaceState({}, '', location.pathname);
        }
    }, [location.search, location.pathname]);

    const formatCurrency = (amount: number) =>
        new Intl.NumberFormat('en-GB', { style: 'currency', currency: 'GBP', maximumFractionDigits: 0 }).format(amount);

    useEffect(() => {
        if (!activeOrgId) return;
        // A month switched again before this run finishes must not let the
        // older, slower response overwrite the newer month's cards.
        let cancelled = false;

        const fetchDetails = async () => {
            const today = new Date();
            const { start: startOfMonth, end: monthEnd } = monthRange(viewMonth);
            // The month in progress is "so far" — same rule as fn_get_period_snapshot.
            const endOfMonth = viewMonth === monthKey(today) ? localDate(today) : monthEnd;
            const { start: startOfLastMonth, end: endOfLastMonth } = monthRange(previousMonthKey(viewMonth));

            // Fetch current + previous month P&L in parallel, recurring bills, and
            // real parts purchases (cash view — excludes synthetic auto-COGS headers
            // so the "outgoings" card doesn't double-count parts sold via invoices).
            const [currentPnl, prevPnl, allBillsRes, partsCashRes] = await Promise.all([
                supabase.rpc('fn_get_profit_and_loss', {
                    p_org_id: activeOrgId,
                    p_start_date: startOfMonth,
                    p_end_date: endOfMonth,
                }),
                supabase.rpc('fn_get_profit_and_loss', {
                    p_org_id: activeOrgId,
                    p_start_date: startOfLastMonth,
                    p_end_date: endOfLastMonth,
                }),
                supabase
                    .from('recurring_expenses')
                    .select('amount, frequency')
                    .eq('org_id', activeOrgId)
                    .eq('is_active', true),
                supabase
                    .from('ledger_line_items')
                    .select('total, header:ledger_headers!inner(transaction_type, is_cogs)')
                    .eq('org_id', activeOrgId)
                    .eq('header.transaction_type', 'expense')
                    .eq('header.is_cogs', false)
                    .eq('category', 'Motor Parts & Consumables')
                    .gte('header_date', startOfMonth)
                    .lte('header_date', endOfMonth),
            ]);
            if (cancelled) return;

            if (currentPnl.data) setPnlData(currentPnl.data as ProfitAndLossData);
            if (prevPnl.data) setLastMonthPnl(prevPnl.data as ProfitAndLossData);

            if (allBillsRes.data) {
                const monthlyTotal = allBillsRes.data.reduce((sum, b) => {
                    let monthlyAmount = b.amount;
                    if (b.frequency === 'weekly') monthlyAmount = b.amount * 4.33;
                    if (b.frequency === 'yearly') monthlyAmount = b.amount / 12;
                    if (b.frequency === 'quarterly') monthlyAmount = b.amount / 3;
                    return sum + monthlyAmount;
                }, 0);
                setTotalRecurring(monthlyTotal);
            }

            if (partsCashRes.data) {
                const cashTotal = partsCashRes.data.reduce(
                    (sum, r) => sum + (Number(r.total) || 0),
                    0,
                );
                setPartsCashSpend(cashTotal);
            }
        };

        if (!dataLoading) {
            setLoading(false);
            fetchDetails();
        }
        return () => { cancelled = true; };
    }, [activeOrgId, dataLoading, viewMonth]);

    // --- Safe to Spend hero body (front, fills the middle) ---
    // A: weekly/daily safe-burn pace based on remaining days in the calendar month
    // B: stacked bar showing how YTD income (net of VAT) split: expenses / tax / NI / safe
    // Headline & pace are driven by NET (gross minus working-capital reservation)
    // — that's the PAYE-equivalent number actually available to the user.
    const today = new Date();
    const daysInMonth = new Date(today.getFullYear(), today.getMonth() + 1, 0).getDate();
    const daysLeft = Math.max(1, daysInMonth - today.getDate() + 1);
    const dailyLimit = Math.max(0, safe_to_spend_net / daysLeft);
    const weeklyLimit = dailyLimit * 7;

    // Underfunded state is computed server-side (fn_get_financial_snapshot) — the
    // frontend only formats it. wcTotal is used for the "reserved" display only.
    const wcTotal = working_capital?.total ?? 0;
    const isUnderfunded = !isHistoricalView && is_underfunded;
    const underfundedBy = underfunded_by;

    const denominator = Math.max(0, total_income);
    const expensesPart = Math.max(0, total_expenses);
    const safePart = Math.max(0, safe_to_spend_net);
    const reservedPart = Math.max(0, Math.min(wcTotal, safe_to_spend));
    const segments = denominator > 0
        ? [
              { key: 'expenses', label: 'Expenses', value: expensesPart, color: 'var(--fd-danger)' },
              { key: 'tax',      label: 'Tax',      value: tax_pot,      color: 'var(--fd-amber)' },
              { key: 'ni',       label: 'NIC',      value: ni_pot,       color: 'color-mix(in oklab, var(--fd-gold) 55%, transparent)' },
              { key: 'paid',     label: 'Paid to you', value: Math.max(0, drawings), color: 'var(--fd-chart-4)' },
              { key: 'wc',       label: 'Reserved', value: reservedPart, color: 'color-mix(in oklab, var(--fd-info) 55%, transparent)' },
              { key: 'safe',     label: 'Safe',     value: safePart,     color: 'var(--fd-success)' },
          ]
        : [];

    const SafeToSpendHeroBody = (
        <div className="flex flex-col gap-5">
            {/* A: pace pill — only when there's actually money to spend, now */}
            {!isHistoricalView && safe_to_spend_net > 0 && (
                <div className="inline-flex items-center gap-2.5 self-start bg-card border border-line rounded-lg px-3.5 py-2">
                    <span className="text-fd-3xs font-semibold uppercase tracking-[0.16em] text-gold">
                        Safe pace
                    </span>
                    <span className="font-mono tabular-nums text-sm text-fg">
                        {formatCurrency(weeklyLimit)}/wk
                    </span>
                    <span className="font-mono tabular-nums text-fd-sm-tight text-fg-3">
                        · {formatCurrency(dailyLimit)}/day · {daysLeft}d left
                    </span>
                </div>
            )}

            {/* Underfunded warning — replaces pace pill when working capital exceeds gross */}
            {isUnderfunded && (
                <div className="inline-flex items-start gap-2.5 self-start bg-danger/10 border border-danger/30 rounded-lg px-3.5 py-2">
                    <Info className="w-3.5 h-3.5 text-danger mt-0.5 shrink-0" />
                    <div className="flex flex-col">
                        <span className="text-fd-3xs font-semibold uppercase tracking-[0.16em] text-danger">
                            Underfunded by {formatCurrency(underfundedBy)}
                        </span>
                        <span className="text-fd-sm-tight text-fg-3 mt-0.5">
                            Hold off on drawings until customers pay.
                        </span>
                    </div>
                </div>
            )}

            {/* VAT breach — HMRC mandatory registration once turnover crosses
                the £90k rolling-12m threshold. Sits next to the pace/underfunded
                pill so it's visible without flipping the card. */}
            {vatBreached && (
                <div className="inline-flex items-start gap-2.5 self-start bg-danger/10 border border-danger/30 rounded-lg px-3.5 py-2">
                    <Info className="w-3.5 h-3.5 text-danger mt-0.5 shrink-0" />
                    <div className="flex flex-col">
                        <span className="text-fd-3xs font-semibold uppercase tracking-[0.16em] text-danger">
                            VAT registration required
                        </span>
                        <span className="text-fd-sm-tight text-fg-3 mt-0.5">
                            Turnover {formatCurrency(turnover_rolling_12m ?? 0)} · {formatCurrency(vatOverBy)} over the {formatCurrency(vat_threshold ?? 0)} threshold. Register within 30 days.
                        </span>
                    </div>
                </div>
            )}

            {/* B: stacked bar of the period's income allocation */}
            {segments.length > 0 ? (
                <div className="flex flex-col gap-3">
                    <div className="flex items-center justify-between text-fd-3xs uppercase tracking-[0.16em] text-fg-3 font-display">
                        <span>Where every £ went</span>
                        <span className="font-mono tabular-nums normal-case tracking-normal">
                            {formatCurrency(denominator)} {isMonthView ? viewMonthLabel : 'YTD'}
                        </span>
                    </div>
                    <div className="flex h-2.5 w-full overflow-hidden rounded-full bg-card border border-line">
                        {segments.map((s) => {
                            const pct = (s.value / denominator) * 100;
                            if (pct <= 0) return null;
                            return (
                                <div
                                    key={s.key}
                                    style={{ width: `${pct}%`, background: s.color }}
                                    title={`${s.label}: ${formatCurrency(s.value)} (${pct.toFixed(0)}%)`}
                                />
                            );
                        })}
                    </div>
                    <div className="flex flex-wrap gap-x-5 gap-y-1.5 text-fd-sm-tight text-fg-3 pt-1">
                        {segments.map((s) => {
                            if (s.value <= 0) return null;
                            return (
                                <span key={s.key} className="inline-flex items-center gap-1.5">
                                    <span className="w-2 h-2 rounded-sm" style={{ background: s.color }} />
                                    <span>{s.label}</span>
                                    <span className="font-mono tabular-nums text-fg">
                                        {formatCurrency(s.value)}
                                    </span>
                                </span>
                            );
                        })}
                    </div>
                </div>
            ) : (
                <div className="text-fd-sm-tight text-fg-4">
                    Log income and expenses to see your allocation.
                </div>
            )}
        </div>
    );

    // --- Safe to Spend back face (transparency + per-pot rules + payable date) ---
    const vatCoverageShort = is_vat_registered
        ? Math.max(0, raw_vat_liability - vat_pot)
        : 0;
    // Refund position: input VAT paid to suppliers exceeds output VAT collected,
    // so the VAT pot is £0 and HMRC owes the mechanic. vat_net_position is
    // collected − paid (negative = refund due).
    const vatRefundDue = is_vat_registered && vat_net_position < 0
        ? Math.abs(vat_net_position)
        : 0;

    const IncomeBack = (
        <div className="flex flex-col">
            <h4 className="text-fd-sm-tight font-semibold text-gold uppercase tracking-[0.18em] mb-5 flex items-center gap-2 font-display">
                <Wallet className="w-4 h-4" /> How this is calculated
            </h4>

            {/* Transparency header */}
            <div className="rounded-lg border border-line bg-card px-3.5 py-2.5 mb-5 text-fd-sm-tight leading-relaxed text-fg-3">
                {isMonthView ? (
                    <>
                        {viewMonthLabel}&apos;s share of the tax-year bill — what this month added to it, like
                        the &ldquo;this period&rdquo; column on a payslip. Tax-year profit to the end of the
                        month:{' '}
                        <span className="font-mono tabular-nums text-fg">{formatCurrency(taxable_profit)}</span>.
                    </>
                ) : (
                    <>
                        Based on{' '}
                        <span className="font-mono tabular-nums text-fg">
                            {formatCurrency(taxable_profit)}
                        </span>{' '}
                        taxable profit since {taxYearStartLabel(today)} ·{' '}
                        <span className="text-gold">{currentTaxYearLabel(today)}</span> HMRC rules.
                    </>
                )}
            </div>

            <div className="space-y-3 flex-1">
                <>
                        {isMonthView && (
                            <>
                                <div className="flex justify-between text-sm text-fg-2 border-b border-line pb-3">
                                    <span>Income</span>
                                    <span className="font-mono text-fg">{formatCurrency(total_income)}</span>
                                </div>
                                <div className="flex justify-between text-sm text-fg-2 border-b border-line pb-3">
                                    <span>Expenses</span>
                                    <span className="font-mono text-fg">{formatCurrency(total_expenses)}</span>
                                </div>
                            </>
                        )}

                        {drawings !== 0 && (
                            <div className="border-b border-line pb-3">
                                <div className="flex justify-between text-sm text-fg-2">
                                    <span>Already paid to you</span>
                                    <span className="font-mono text-fg">{formatCurrency(drawings)}</span>
                                </div>
                                <div className="text-fd-3xs text-fg-3 mt-1.5">
                                    Pay Yourself · comes off Safe to Spend, not off your tax
                                </div>
                            </div>
                        )}

                        {/* HMRC Reserves group — the period's pots */}
                        <div className="text-fd-3xs font-semibold text-fg-4 uppercase tracking-[0.2em] font-display pb-1">
                            HMRC Reserves · {isMonthView ? viewMonthLabel : currentTaxYearLabel(today)}
                        </div>
                        <div className="border-b border-line pb-3">
                            <div className="flex justify-between text-sm text-fg-2">
                                <span>Income Tax held</span>
                                <span className="font-mono text-fg">{formatCurrency(tax_pot)}</span>
                            </div>
                            <div className="text-fd-3xs text-fg-3 mt-1.5">
                                {incomeTaxBandLabel(taxable_profit, tax_region)}
                            </div>
                        </div>
                        <div className="border-b border-line pb-3">
                            <div className="flex justify-between text-sm text-fg-2">
                                <span>NIC Class 4 held</span>
                                <span className="font-mono text-fg">{formatCurrency(ni_pot)}</span>
                            </div>
                            <div className="text-fd-3xs text-fg-3 mt-1.5">
                                {nicBandLabel(taxable_profit)}
                            </div>
                        </div>
                        <div className="border-b border-line pb-3">
                            <div className="flex justify-between text-sm text-fg-2">
                                <span>VAT held</span>
                                <span className="font-mono text-fg">{formatCurrency(vat_pot)}</span>
                            </div>
                            <div className="flex justify-between text-fd-3xs mt-1.5">
                                <span className={vatBreached ? 'text-danger font-semibold' : 'text-fg-3'}>
                                    {vatBreached
                                        ? `Over threshold by ${formatCurrency(vatOverBy)} — register within 30d`
                                        : vatBandLabel(is_vat_registered)}
                                </span>
                                {vatCoverageShort > 0 && (
                                    <span className="text-danger">
                                        short by {formatCurrency(vatCoverageShort)}
                                    </span>
                                )}
                            </div>
                            {vatRefundDue > 0 && (
                                <div className="text-fd-3xs text-success mt-1.5">
                                    HMRC owes you {formatCurrency(vatRefundDue)} — input VAT exceeds VAT collected
                                </div>
                            )}
                        </div>

                        {/* Working Capital group — current snapshot, not YTD;
                            a month that has ended has nothing to reserve. */}
                        {!isHistoricalView && (<>
                        <div className="text-fd-3xs font-semibold text-fg-4 uppercase tracking-[0.2em] font-display pt-3 pb-1 flex items-center gap-1.5">
                            <Lock className="w-3 h-3" />
                            Working Capital · now &amp; next 30d
                        </div>
                        <div className="border-b border-line pb-3">
                            <div className="flex justify-between text-sm text-fg-2">
                                <span>Parts still to buy</span>
                                <span className="font-mono text-fg">{formatCurrency(working_capital?.parts_reserved ?? 0)}</span>
                            </div>
                            <div className="text-fd-3xs text-fg-3 mt-1.5">
                                Quoted on active jobs · less supplier spend logged
                            </div>
                        </div>
                        {(working_capital?.parts_unreconciled ?? 0) > 0 && (
                            <div className="border-b border-line pb-3">
                                <div className="flex justify-between text-sm text-fg-2">
                                    <span>Parts awaiting supplier bill</span>
                                    <span className="font-mono text-gold">{formatCurrency(working_capital.parts_unreconciled)}</span>
                                </div>
                                <div className="text-fd-3xs text-fg-3 mt-1.5">
                                    Job paid · supplier invoice not yet uploaded
                                </div>
                            </div>
                        )}
                        <div className="border-b border-line pb-3">
                            <div className="flex justify-between text-sm text-fg-2">
                                <span>Recurring bills (30d)</span>
                                <span className="font-mono text-fg">{formatCurrency(working_capital?.bills_due_30d ?? 0)}</span>
                            </div>
                            <div className="text-fd-3xs text-fg-3 mt-1.5">
                                Rolling forward — refreshes daily
                            </div>
                        </div>
                        <div className="border-b border-line pb-3">
                            <div className="flex justify-between text-sm text-fg font-medium">
                                <span>Total reserved</span>
                                <span className="font-mono">{formatCurrency(wcTotal)}</span>
                            </div>
                        </div>
                        </>)}
                </>
            </div>

            {/* Payable-by */}
            {!isHistoricalView && (
                <div className="mt-5 flex items-center gap-2 text-fd-sm-tight text-fg-3 rounded-lg border border-line bg-card px-3.5 py-2.5">
                    <CalendarClock className="w-3.5 h-3.5 text-gold" />
                    <span>
                        Tax &amp; NIC payable by{' '}
                        <span className="text-fg font-medium">
                            {selfAssessmentDueLabel(today)}
                        </span>
                    </span>
                </div>
            )}

            {/* Caveats — own the rough edges honestly */}
            <div className="mt-3 flex items-start gap-1.5 text-fd-3xs text-fg-4 leading-snug">
                <Info className="w-3 h-3 mt-0.5 shrink-0" />
                <span>
                    {tax_region === 'scotland' ? 'Scottish rates.' : 'Rest-of-UK rates.'} Updates in real time.
                    {isMonthView && ' Months add up to the tax-year figure.'}
                </span>
            </div>
        </div>
    );

    // --- Empty-state detection ---
    // True when the org has no income AND no expense activity in the active period.
    // Drives the "Complete your first job" copy on Safe to Spend + dash placeholders.
    const hasNoActivity =
        !loading && !moneyUnavailable && total_income === 0 && total_expenses === 0 && gross_expenses === 0;

    // --- Expenses delta vs last month ---
    // Green when expenses go DOWN (good), red when they go UP (bad). Opposite of revenue.
    const expensesTrend = useMemo(() => {
        const current = pnlData?.expense_total ?? gross_expenses;
        const prev = lastMonthPnl?.expense_total ?? 0;
        if (!prev) return undefined; // no prior month data — show nothing rather than a meaningless 100%
        const deltaPct = ((current - prev) / prev) * 100;
        const up = deltaPct >= 0;
        const arrow = up ? '↑' : '↓';
        const rounded = Math.abs(deltaPct).toFixed(0);
        return {
            value: `${arrow} ${rounded}%`,
            label: 'vs last month',
            color: up ? ('red' as const) : ('emerald' as const),
        };
    }, [pnlData, lastMonthPnl, gross_expenses]);

    // --- Expenses back face ---
    // partsTotal is *real cash purchases* (excludes auto-COGS) — this is a
    // cash-outflow view, not P&L COGS.
    const partsTotal = partsCashSpend;
    const opexTotal = pnlData?.opex_total || 0;

    const ExpensesBack = (
        <div className="flex flex-col">
            <h4 className="text-fd-sm-tight font-semibold text-fg-3 uppercase tracking-[0.18em] mb-4 flex items-center gap-2 font-display">
                <FileText className="w-4 h-4" /> {viewMonthLabel} outgoings
            </h4>
            <div className="space-y-1 flex-1">
                <div className="flex justify-between text-sm text-fg-2 border-b border-line py-2.5">
                    <span>Motor Parts &amp; Consumables</span>
                    <span className="font-mono text-fg">{formatCurrency(partsTotal)}</span>
                </div>
                <div className="flex justify-between text-sm text-fg-2 border-b border-line py-2.5">
                    <span>Total Recurring Bills</span>
                    <span className="font-mono text-fg">{formatCurrency(totalRecurring)}</span>
                </div>
                <div className="flex justify-between text-sm text-fg-2 border-b border-line py-2.5">
                    <span>General Running Costs</span>
                    <span className="font-mono text-fg">{formatCurrency(opexTotal)}</span>
                </div>
            </div>
        </div>
    );

    // Safe-to-Spend breakdown lives in a modal now (roomier than a card back):
    // the warnings + "where every £ went" bar, then the per-pot detail.
    const SafeToSpendDetail = moneyUnavailable ? (
        <p className="text-sm text-fg-3">
            We couldn&apos;t work out your Safe to Spend just now, so no figures are shown.
            Nothing is lost — try again in a minute.
        </p>
    ) : (
        <div className="flex flex-col gap-6">
            {!hasNoActivity && SafeToSpendHeroBody}
            {IncomeBack}
        </div>
    );

    return (
        <>
            {/* THE FOUR MONEY ACTIONS */}
            <QuickActions
                onNewAppointment={() => setIsBookingModalOpen(true)}
                onNewDiagnostic={() => setIsDiagnosticModalOpen(true)}
                onNewQuote={() => setIsQuoteModalOpen(true)}
                onNewJob={() => setIsNewJobModalOpen(true)}
            />

            {!isNewJobModalOpen && (
                <ResumeJobBanner
                    refreshKey={todayRefreshKey}
                    onResume={(jobId) => {
                        setResumeJobId(jobId);
                        setIsNewJobModalOpen(true);
                    }}
                />
            )}

            {/* Work first, then money, then setup — see .fd-dash in
                styles/layout.css for how this redistributes by width. */}
            <div className="fd-dash">
                <div className="fd-dash-jobs fd-card-in" style={{ animationDelay: '0ms' }}>
                    <JobsToDoCard onNewJob={() => setIsNewJobModalOpen(true)} />
                </div>
                <div className="fd-dash-appts fd-card-in" style={{ animationDelay: '50ms' }}>
                    <AppointmentsCard onBookSlot={() => setIsBookingModalOpen(true)} refreshKey={todayRefreshKey} />
                </div>
                {/* NEEDS ATTENTION — actions owed today; self-hides to "all clear" */}
                <div className="fd-dash-attn fd-card-in" style={{ animationDelay: '100ms' }}>
                    <NeedsAttentionCard />
                </div>

                <aside className="fd-dash-rail" aria-label="Money">
                    <p className="fd-dash-section-label fd-eyebrow">Money</p>
                    {/* Safe to Spend · Expenses · This Month (tap any for detail) */}
                    <div className="fd-dash-money">
                        <div className="fd-card-in" style={{ animationDelay: '150ms' }}>
                            <MetricCard
                                featured
                                title="Safe to Spend"
                                detailTitle={isMonthView ? `Safe to Spend · ${viewMonthLabel}` : 'Safe to Spend'}
                                mainFigure={hasNoActivity || moneyUnavailable ? '—' : formatCurrency(safe_to_spend_net)}
                                subline={
                                    moneyUnavailable ? (
                                        <span className="text-danger normal-case tracking-normal block">
                                            Can&apos;t calculate right now
                                        </span>
                                    ) : hasNoActivity && !isMonthView ? (
                                        <span className="text-fg-4 normal-case tracking-normal block">
                                            Complete your first job and this comes to life.
                                        </span>
                                    ) : isHistoricalView ? (
                                        <span className="text-fg-3 normal-case tracking-normal block">
                                            {viewMonthLabel} · after tax &amp; NI
                                        </span>
                                    ) : safe_to_spend_net > 0 ? (
                                        <span className="text-fg-3 normal-case tracking-normal block">
                                            {formatCurrency(weeklyLimit)}/wk safe pace · {daysLeft}d left
                                        </span>
                                    ) : !isHistoricalView && wcTotal > 0 ? (
                                        <span className="text-fg-3 normal-case tracking-normal block">
                                            {formatCurrency(wcTotal)} reserved for work in progress
                                        </span>
                                    ) : undefined
                                }
                                icon={PiggyBank}
                                backContent={SafeToSpendDetail}
                                loading={loading}
                                frontAction={
                                    moneyUnavailable ? (
                                        <button onClick={() => refresh()} className="fd-btn">
                                            Try again
                                        </button>
                                    ) : !isHistoricalView && !hasNoActivity ? (
                                        <button
                                            onClick={() => setIsDrawingModalOpen(true)}
                                            className="fd-btn fd-btn-brass"
                                        >
                                            <Wallet />
                                            Pay Yourself
                                        </button>
                                    ) : undefined
                                }
                            />
                        </div>
                        <div className="fd-card-in" style={{ animationDelay: '200ms' }}>
                            <MetricCard
                                title="Expenses"
                                mainFigure={hasNoActivity || (isMonthView && moneyUnavailable) ? '—' : formatCurrency(gross_expenses)}
                                trend={expensesTrend}
                                icon={TrendingDown}
                                backContent={ExpensesBack}
                                loading={loading}
                            />
                        </div>
                        <div className="fd-card-in" style={{ animationDelay: '250ms' }}>
                            <ThisMonthCard month={viewMonth} pnlData={pnlData} lastMonthPnl={lastMonthPnl} loading={loading} />
                        </div>
                    </div>
                    {setup}
                </aside>
            </div>

            <RecordDrawingModal
                isOpen={isDrawingModalOpen}
                onClose={() => setIsDrawingModalOpen(false)}
                onSuccess={() => {
                    setIsDrawingModalOpen(false);
                    // Don't wait on the realtime cache event to show the new
                    // headline — re-read the engine straight away.
                    void refresh();
                }}
            />

            <BookingModal
                isOpen={isBookingModalOpen}
                onClose={() => setIsBookingModalOpen(false)}
                onSuccess={() => {
                    setIsBookingModalOpen(false);
                    setTodayRefreshKey((k) => k + 1);
                }}
            />

            {/* New Job runs start to finish here: form, workspace, invoice. */}
            {isNewJobModalOpen && (
                <NewJobFlow
                    resumeJobId={resumeJobId}
                    onClose={() => {
                        setIsNewJobModalOpen(false);
                        setResumeJobId(null);
                        setTodayRefreshKey((k) => k + 1);
                    }}
                    onChanged={() => setTodayRefreshKey((k) => k + 1)}
                />
            )}

            <DiagnosticModal
                isOpen={isDiagnosticModalOpen}
                onClose={() => setIsDiagnosticModalOpen(false)}
                onSaved={() => setTodayRefreshKey((k) => k + 1)}
            />

            {/* Mounted only while open, so every New Quote starts blank. */}
            {isQuoteModalOpen && (
                <QuoteBuilder
                    variant="modal"
                    onClose={() => setIsQuoteModalOpen(false)}
                    onJobCreated={() => setTodayRefreshKey((k) => k + 1)}
                    onOpenJob={(jobId) => {
                        setIsQuoteModalOpen(false);
                        navigate(`/workshop?job_id=${jobId}`);
                    }}
                />
            )}
        </>
    );
};

